'use client';

import React, { useState } from 'react';
import { Phone, ChevronDown } from 'lucide-react';
import { Skeleton } from '@/shared/components/skeletons';
import { useGetApprovedNumbersQuery, type ApprovedNumber } from '@/api/features/compliance';

const AvailablePhoneNumbers: React.FC = () => {
  const { data: approvedNumbers, isLoading, error } = useGetApprovedNumbersQuery();
  const [isExpanded, setIsExpanded] = useState(false);

  const formatPhoneNumber = (phoneNumber: string): string => {
    const digits = phoneNumber.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.substring(0, 3)})${digits.substring(3, 6)}-${digits.substring(6, 10)}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return `(${digits.substring(1, 4)})${digits.substring(4, 7)}-${digits.substring(7, 11)}`;
    }
    return phoneNumber;
  };

  const formatType = (type: string): string => {
    switch (type) {
      case 'new': return 'New Number';
      case 'host': return 'Hosted';
      case 'port': return 'Ported';
      default: return type;
    }
  };

  const count = approvedNumbers?.length ?? 0;

  return (
    <div className="bg-card-background rounded-lg border border-border">
      {/* Collapsible header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-hover-background/50 transition-colors rounded-lg"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-text-secondary" />
          <h3 className="text-sm font-medium text-text-primary">Manage Numbers</h3>
          {count > 0 && (
            <span className="text-xs text-text-secondary bg-hover-background px-1.5 py-0.5 rounded">
              {count}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-secondary hidden sm:inline">
            Approved numbers &amp; registrations
          </span>
          <ChevronDown className={`h-4 w-4 text-text-secondary transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border pt-3">
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          ) : error ? (
            <p className="text-sm text-text-secondary">Failed to load available phone numbers.</p>
          ) : !approvedNumbers || approvedNumbers.length === 0 ? (
            <p className="text-sm text-text-secondary">No additional approved phone numbers.</p>
          ) : (
            <div className="space-y-2">
              {approvedNumbers.map((number: ApprovedNumber) => (
                <div
                  key={number.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-hover-background transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Phone className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-text-primary">
                        {formatPhoneNumber(number.phoneNumber)}
                      </div>
                      <div className="text-xs text-text-secondary">
                        {number.tenDlcApplicationName}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                      {formatType(number.type)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AvailablePhoneNumbers;
